import { useState, useContext } from "react";
import ModalContext from "../../../../../contexts/modal";
import { usePackages } from "../../../../../hooks/usePackages";
import { translate } from "../../../../../utils/Common";
import Input from "../../../../../components/common/Input";
import Button from "../../../../../components/common/Button";
import { FaKey, FaBox, FaSearch, FaTimes } from "react-icons/fa";
import styles from "./SearchPackages.module.css";
import { verifyAccessViewPermissions } from "../../../../../config/permissions";

const SEARCH_BY_ACCESS_CODE = false;
const SEARCH_BY_PACKAGE_NUMBER = true;

const SearchPackage = ({ setPackageInfo }) => {
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [typeSearch, setTypeSearch] = useState(SEARCH_BY_ACCESS_CODE);
  const [isLoading, setIsLoading] = useState(false);
  const modalContext = useContext(ModalContext);
  const { getPackages } = usePackages();
  const permissionsPath = "/dashboard/platforms/support";
  const canSearch = verifyAccessViewPermissions(["search"], { path: permissionsPath });

  const handleSearch = async () => {
    try {
      if (!canSearch) return;
      setError("");
      setIsLoading(true);
      const response = await getPackages({ value: search, type: typeSearch });
      if (response.length > 0) {
        setPackageInfo(response[0]);
      } else {
        setError(translate("dashboard.packages.search.NOT_PACKAGE_FOUND"));
      }
    } catch (err) {
      console.log(err);
      modalContext.confirm(
        translate("global.alerts.ops"),
        <span>{translate("dashboard.platforms.details.error")}</span>,
        <button
          type="button"
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 h-10 px-4 py-2 h-8 rounded-md px-3 text-xs bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/90"
          onClick={() => modalContext.setIsOpenModal(false)}
        >
          {translate("global.accept")}
        </button>,
        true
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearInput = () => {
    if (!canSearch) return;
    setPackageInfo(null);
    setSearch("");
    setError("");
  };

  const placeholder = typeSearch === SEARCH_BY_ACCESS_CODE
    ? translate("dashboard.platforms.support.search.placeholderAccessCode")
    : translate("dashboard.platforms.support.search.placeholderPackageNumber");

  const inputLabel = typeSearch === SEARCH_BY_ACCESS_CODE
    ? translate("dashboard.platforms.support.search.byAccessCode")
    : translate("dashboard.platforms.support.search.byPackageNumber");

  return (
    <div className={styles.searchCard}>
      <div className={styles.header}>
        <h2 className={styles.title}>
          {translate("dashboard.platforms.support.search.title")}
        </h2>
        <p className={styles.subtitle}>
          {translate("dashboard.platforms.support.search.subtitle")}
        </p>
      </div>

      {!canSearch ? (
        <div style={{ marginTop: 12, color: "var(--text-secondary, #6B7280)" }}>
          {translate("dashboard.clients.formSearch.NOT_ACCESS") !==
          "dashboard.clients.formSearch.NOT_ACCESS"
            ? translate("dashboard.clients.formSearch.NOT_ACCESS")
            : "No tienes acceso para buscar."}
        </div>
      ) : (
      <div className={styles.searchRow}>
        <div className={styles.inputSection}>
        <Input
          type="text"
          label={inputLabel}
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          error={error}
          fullWidth
          size="md"
          leftIcon={<FaSearch size={14} className={styles.inputIcon} />}
        />
        <div className={styles.actionsGroup}>
          <Button
            variant="primary"
            size="md"
            leftIcon={<FaSearch size={14} />}
            onClick={handleSearch}
            disabled={!search.trim()}
            loading={isLoading}
          >
            {translate("dashboard.platforms.support.search.findPackage")}
          </Button>
          <Button
            variant="outline"
            size="md"
            leftIcon={<FaTimes size={14} />}
            onClick={handleClearInput}
          >
            {translate("global.clean")}
          </Button>
        </div>
        </div>

        <div className={styles.optionsRow}>
        <button
          type="button"
          className={`${styles.optionCard} ${!typeSearch ? styles.optionCardActive : ""}`}
          onClick={() => {
            setTypeSearch(SEARCH_BY_ACCESS_CODE);
            setError("");
          }}
          aria-pressed={!typeSearch}
          aria-label={translate("dashboard.platforms.support.search.byAccessCode")}
        >
          <span className={styles.optionIcon}>
            <FaKey size={12} aria-hidden />
          </span>
          <span className={styles.optionTitle}>
            {translate("dashboard.platforms.support.search.byAccessCode")}
          </span>
          <span className={styles.optionHelp}>
            {translate("dashboard.platforms.support.search.byAccessCodeHelp")}
          </span>
        </button>

        <button
          type="button"
          className={`${styles.optionCard} ${typeSearch ? styles.optionCardActive : ""}`}
          onClick={() => {
            setTypeSearch(SEARCH_BY_PACKAGE_NUMBER);
            setError("");
          }}
          aria-pressed={typeSearch}
          aria-label={translate("dashboard.platforms.support.search.byPackageNumber")}
        >
          <span className={styles.optionIcon}>
            <FaBox size={16} aria-hidden />
          </span>
          <span className={styles.optionTitle}>
            {translate("dashboard.platforms.support.search.byPackageNumber")}
          </span>
          <span className={styles.optionHelp}>
            {translate("dashboard.platforms.support.search.byPackageNumberHelp")}
          </span>
        </button>
        </div>
      </div>
      )}
    </div>
  );
};

export default SearchPackage;
